import { routes } from "@/routes";
import { formRegEx, formRegExReplacer } from "constant/regex";
import moment from "moment";
import { toast } from "react-toastify";
import {
  CURRENCY,
  MAIN_PERMISSION,
  notProtectedRoute,
  permissionRoute,
  SUB_PERMISSION,
} from "../constant/constants";

export const thousandToK = (num) => {
  if (num > 999 && num < 1000000) {
    return (num / 1000).toFixed(1) + "K";
  } else if (num > 1000000) {
    return (num / 1000000).toFixed(1) + "M";
  } else if (num < 900) {
    return num;
  }
};

export const getUserName = (user) => {
  if (!user) return "N/A";
  return (user?.firstName || "") + " " + (user?.lastName || "");
};

export const formatMessageDate = (date) => {
  const messageDate = moment(date);
  const now = moment();

  if (messageDate.isSame(now, "minute")) {
    // If the date is exactly now, show the current time, e.g., 'HH:mm A'
    return now.format("HH:mm A");
  } else if (messageDate.isSame(now, "day")) {
    // If the date is today, use fromNow()
    return messageDate.fromNow();
  } else if (messageDate.isSame(now.subtract(1, "day"), "day")) {
    // If the date is yesterday, use fromNow()
    return messageDate.fromNow();
  } else {
    // Otherwise, show the date in a standard format, e.g., 'MM/DD/YYYY'
    return messageDate.format("MM/DD/YYYY");
  }
};

export const formatDate = (date, format = "MM/DD/YYYY") => {
  const formdatedDate = moment(date);
  return formdatedDate.format(format);
};

export const formatTime = (time) => {
  const formattedTime = moment(time);
  return formattedTime.format("hh:mm A");
};
export function stripAllHtmlTags(input) {
  return input?.replace(/<[^>]*>?/gm, "");
}

const options = {
  onDownloadProgress: (progressEvent) => {
    const { loaded, total } = progressEvent;
  },
};

export const scrollToTop = (topVal = 0) => {
  window.scrollTo({
    top: topVal,
    behavior: "smooth",
  });
};

export const formatNumber = (num) => {
  const number = Number(num);
  if (!number) return "0";
  if (number % 1 === 0) {
    return `${number?.toLocaleString()} ${CURRENCY}`;
  } else {
    return `${number?.toFixed(2)?.toLocaleString()} ${CURRENCY}`;
  }
};

export const apiHeader = (token, isFormData) => {
  const headers = {
    "ngrok-skip-browser-warning": "69420",
    "timezone-offset": moment().utcOffset(),
    "Content-Type": isFormData ? "multipart/form-data" : "application/json",
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return { headers };
};

export const CreateFormData = (data) => {
  const formData = new FormData();
  for (let key in data) {
    formData.append(key, data[key]);
  }
  return formData;
};

export const quillValidateHandler = (
  params,
  toastMessage = "Please fill the home cms field!",
) => {
  let flag = true;
  for (let key in params) {
    const parsedHTML = new DOMParser().parseFromString(
      params[key],
      "text/html",
    );
    const plainText = parsedHTML.body.textContent || "";
    if (plainText?.trim() === "") {
      toast.error(toastMessage);
      flag = false;
      break;
    }
  }
  return flag;
};

export const validateDate = (date) => {
  if (!date) return null; // Return null if date is falsy

  // Check if date is a moment object and has a $d property
  if (date?.$d) {
    return moment(date.$d).format(); // Format if $d exists
  } else {
    return moment(date).format(); // Assume date is a valid input for moment
  }
};

export const validateProductLinks = (links) => {
  for (let i = 0; i < links.length; i++) {
    const link = links[i];
    for (let linkKey in link) {
      if (!link[linkKey]) {
        toast.error(`${linkKey} cannot be empty for link ${i + 1}`);
        return false;
      }
    }
  }
  return true;
};

export const validateUrl = (url) => {
  const urlRegex =
    /^(https?:\/\/)([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
  return urlRegex.test(url);
};

export const urlPattern =
  /^(https?:\/\/)(www\.)?([\w-]+\.)+[a-z]{2,6}(\/[\w-]*)*\/?$/;

export const camelCaseToLower = (str) => {
  return str.replace(formRegEx, formRegExReplacer).toLowerCase();
};

export const getYearRange = () => {
  const yearOptions = [];
  const currentYear = new Date().getFullYear();

  for (let i = 0; i < 6; i++) {
    const year = currentYear - i;
    yearOptions.push({
      label: year,
      value: year,
    });
  }

  return yearOptions;
};

export const validateNestedParams = (
  params,
  parentKey = "",
  options = { disallowEmptyArray: true },
) => {
  const formatKey = (rawKey) => {
    return rawKey
      .replace(/\[(\d+)\]/g, (_, i) => ` ${parseInt(i) + 1}`) // [0] => 1
      .split(".")
      .map(
        (part) =>
          part
            .replace(/_/g, " ")
            .replace(/\b\w/g, (char) => char.toUpperCase()), // capitalize
      )
      .join(" → ");
  };

  if (Array.isArray(params)) {
    if (options.disallowEmptyArray && params.length === 0) {
      toast.error(`${formatKey(parentKey)} cannot be empty`);
      return false;
    }

    for (let i = 0; i < params.length; i++) {
      const key = `${parentKey}[${i}]`;
      if (!validateNestedParams(params[i], key, options)) return false;
    }
  } else if (typeof params === "object" && params !== null) {
    const keys = Object.keys(params);
    if (keys.length === 0 && parentKey) {
      toast.error(`${formatKey(parentKey)} cannot be empty`);
      return false;
    }

    for (const key of keys) {
      const fullKey = parentKey ? `${parentKey}.${key}` : key;
      if (!validateNestedParams(params[key], fullKey, options)) return false;
    }
  } else {
    if (params === "" || params === null || params === undefined) {
      toast.error(`${formatKey(parentKey)} cannot be empty`);
      return false;
    }
  }

  return true;
};

export const formatHours = (hours) => {
  return `${hours} Hr${hours > 1 ? "s" : ""}`;
};

export function checkPermission(permissions, main, sub) {
  const permission = permissions?.find((item) => item?.main === main);
  if (permission) {
    return permission?.sub?.includes(sub);
  }
  return false;
}

export const handleCheckPermission = (permissionsArray) => {
  const permission = MAIN_PERMISSION.ROLE;
  const obj = {
    [SUB_PERMISSION.CREATE]: false,
    [SUB_PERMISSION.UPDATE]: false,
    [SUB_PERMISSION.DELETE]: false,
  };
  for (const key in obj) {
    if (checkPermission(permissionsArray, permission, key)) {
      obj[key] = true;
    }
  }
  return obj;
};

export const getFirstRoute = (url) => {
  // Check if the URL is just "/"
  if (url === "/") {
    return "/";
  }

  // Split the URL by '/' and filter out empty values
  const routes = url.split("/").filter((route) => route);

  // Return the first route as '/route'
  return `/${routes?.[0]}`;
};

export const filterRoutes = (permissions) => {
  const filteredRoutes = [];
  const filteredPermissions = permissions?.map((item) => item?.main);

  for (const item of routes) {
    const firstRoute = getFirstRoute(item.path);
    if (notProtectedRoute.includes(firstRoute)) {
      filteredRoutes.push(item);
    } else {
      const isArray = Array.isArray(permissionRoute[firstRoute]);
      const permission = isArray
        ? filteredPermissions?.some((permission) =>
            permissionRoute[firstRoute]?.includes(permission),
          )
        : filteredPermissions?.includes(permissionRoute[firstRoute]);
      if (permission) {
        filteredRoutes.push(item);
      }
    }
  }
  return filteredRoutes;
};

export const capitalizeFirstLetter = (string) => {
  if (!string) return "";
  return string.charAt(0).toUpperCase() + string.slice(1);
};
export const permissionChecking = (permissions, mainPermission) => {
  const createPermission = checkPermission(
    permissions,
    mainPermission,
    SUB_PERMISSION.CREATE,
  );
  const updatePermission = checkPermission(
    permissions,
    mainPermission,
    SUB_PERMISSION.UPDATE,
  );
  const deletePermission = checkPermission(
    permissions,
    mainPermission,
    SUB_PERMISSION.DELETE,
  );
  return {
    create: createPermission,
    update: updatePermission,
    delete: deletePermission,
  };
};

export const getID = (id) => (id ? id.slice(id.length - 7, id.length) : "");
